// src/ConferenceRegistration.jsx
import React, { useEffect, useState, useRef } from "react";
//import "../index.css";
import "../../ConferenceRegistration.css";
import TermsAndConditions from "../terms/TermsAndConditions";
import {
  AbstractIntentSelector,
  AbstractSubmissionFields,
  PaperLockedNotice,
  PaymentCompletedNotice,
} from "../abstract/AbstractSubmissionModule";
import {
  getPaymentAmount,
  getSuccessfulPayments,
  hasSuccessfulPaymentRecord,
} from "../payment/paymentCalculations";
import {
  fetchPaperCount,
  fetchUserRegistrations,
  saveConferenceRegistration,
} from "../api/conferenceRegistrationApi";
import {
  getNextStepAfterAddress,
  isAbstractModuleEnabled,
  isInlineAbstractStepEnabled,
  isPostPaymentAbstractAvailable,
  isPostPaymentAbstractEnabled,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
  shouldSkipInlineAbstractStep,
} from "../registration/registrationModules";
import {
  validateRegistrationStep,
} from "../registration/registrationValidation";
import {
  CONFERENCE_API,
  CONFERENCE_REGISTRATION_URL,
  currentHost,
  logRegistrationEnvironment,
  MOBILE_MAX_LENGTH,
  ORDER_URL,
  PAYMENT_SAVE_URL,
  RAZORPAY_KEY_ID,
  REGISTRATION_BASE_URL,
  TOTAL_STEPS,
} from "../config/registrationConfig";
import {
  buildConferenceRegistrationPayload as buildRegistrationPayload,
} from "../payloads/registrationPayloadFactory";
import { useAccompanyingPersons } from "../hooks/useAccompanyingPersons";
import { useCategoryHistory } from "../hooks/useCategoryHistory";
import { useCoAuthors } from "../hooks/useCoAuthors";
import { useExistingRegistrationCheck } from "../hooks/useExistingRegistrationCheck";
import { useExistingRegistrationSync } from "../hooks/useExistingRegistrationSync";
import { useFocusedSessions } from "../hooks/useFocusedSessions";
import { usePaperMetadata } from "../hooks/usePaperMetadata";
import { usePaperSubmissionState } from "../hooks/usePaperSubmissionState";
import { useRegistrationContactValidation } from "../hooks/useRegistrationContactValidation";
import { useRegistrationFees } from "../hooks/useRegistrationFees";
import { useRegistrationLookups } from "../hooks/useRegistrationLookups";
import { useRegistrationNavigation } from "../hooks/useRegistrationNavigation";
import { useRegistrationPayment } from "../hooks/useRegistrationPayment";
import { useRegistrationSave } from "../hooks/useRegistrationSave";
import { useStudentIdCardUpload } from "../hooks/useStudentIdCardUpload";
import { useTermsAgreement } from "../hooks/useTermsAgreement";
import {
  AccompanyingPersonsSummarySection,
  AddressSummarySection,
  PersonalSummarySection,
  PaymentStatusSummarySection,
  PaidFocusedSessionsSummarySection,
  ProfessionalSummarySection,
  RegistrationDetailsSummarySection,
  RegistrationFeeSummarySection,
  SummaryTermsAndActionsSection,
  TotalAmountSummarySection,
  ZeroPaymentsSummarySection,
} from "../summarySections";
import {
  AddressStep,
  AccompanyingPersonsInputSection,
  CategoryRegistrationSelectionSection,
  DepartmentSelectionSection,
  FocusedSessionsInputSection,
  InlineAbstractSubmissionStep,
  PaperSubmissionReviewStep,
  PersonalInfoStep,
  ProgressBar,
  ProfessionalInfoStep,
  RegistrationDocumentFieldsSection,
  RegistrationRequirementNotice,
} from "../registrationSteps";

logRegistrationEnvironment();

const ConferenceRegistration = ({
  existingUser,
  prefilledEmail,
  onBackToLogin,
}) => {
  const [step, setStep] = useState(1);
  const [isExistingUser, setIsExistingUser] = useState(!!existingUser);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [abstractMetadataLoaded, setAbstractMetadataLoaded] = useState(false);
  const [navigationDecisionVersion, setNavigationDecisionVersion] = useState(0);
  const [resumePostPaymentAbstract, setResumePostPaymentAbstract] =
    useState(false);

  // Conveyance fee state (percentage from API)
  const [conveyanceFeePercentage, setConveyanceFeePercentage] = useState(null);

  const {
    abstractStepNumber,
    setAbstractStepNumber,
    abstractConfig,
    setAbstractConfig,
    paperFile,
    setPaperFile,
    paperFileError,
    setPaperFileError,
    abstractText,
    setAbstractText,
    paperTitle,
    setPaperTitle,
    selectedTrack,
    setSelectedTrack,
    selectedTrackName,
    presentationMode,
    setPresentationMode,
    submissionMethod,
    setSubmissionMethod,
    tracks,
    setTracks,
    maxAbstractChars,
    setMaxAbstractChars,
    maxFileSizeMb,
    setMaxFileSizeMb,
    wantsToSubmitAbstract,
    setWantsToSubmitAbstract,
    isPaperLocked,
    setIsPaperLocked,
    paperFileUploaded,
    setPaperFileUploaded,
    paperFileName,
    setPaperFileName,
    submittedPapers,
    setSubmittedPapers,
    existingPaperId,
    setExistingPaperId,
    hasPaperSubmitted,
    setHasPaperSubmitted,
    paperApprovalStatus,
    setPaperApprovalStatus,
    paperSubmissionAction,
    setPaperSubmissionAction,
    showPaperSubmissionSummary,
    setShowPaperSubmissionSummary,
    handleUploadedPaperFileDelete,
    handleSelectedPaperFileRemove,
    handleInlineAbstractTextChange,
    resetPaperFormForNewSubmission,
  } = usePaperSubmissionState();
  const isApprovalMandateEnabled = abstractConfig?.approvalMandate === true;
  const {
    coAuthors,
    setCoAuthors,
    coAuthorErrors,
    handleAddCoAuthor,
    handleAddValidatedCoAuthor,
    handleUpdateCoAuthor,
    handleUpdateValidatedCoAuthor,
    handleRemoveCoAuthor,
    handleRemoveValidatedCoAuthor,
  } = useCoAuthors();

  // Track if registration has been updated (for payment validation)
  const [registrationJustUpdated, setRegistrationJustUpdated] = useState(false);
  const {
    accompanyingPersons,
    setAccompanyingPersons,
    accompanyingPersonFee,
    setAccompanyingPersonFee,
    handleAddAccompanyingPerson,
    handleRemoveAccompanyingPerson,
    handleAccompanyingPersonNameChange,
    restoreAccompanyingPersonsFromFees,
    syncAccompanyingPersonsWithReloadedRegistration,
  } = useAccompanyingPersons({ setRegistrationJustUpdated });

  // Ref to track if checkExistingRegistration has loaded conference data
  const conferenceDataLoadedRef = useRef(false);

  // Refs to store navigation decision data (for handling race condition with abstractStepNumber)
  const navigationDecisionRef = useRef({
    isPaid: false,
    hasPaperSubmitted: false,
    paperStatus: null,
    ready: false,
  });
  const registrationCheckRequestIdRef = useRef(0);

  const isPendingPaperApprovalStatus = (status) => {
    const normalized = (status || '').trim().toUpperCase();
    if (!normalized) return false;

    return [
      'SUBMITTED',
      'UNDER_REVIEW',
      'REVIEWED',
      'PENDING',
    ].includes(normalized);
  };

  // Computed: Is abstract/paper submission required? Based on abstractStepNumber
  // abstractStepNumber: 0 = not required/disabled, 3 = required at step 3, 7 = required at step 7
  const isAbstractRequired = isAbstractModuleEnabled(abstractStepNumber);

  // Helper function to render category/registration type history as pills/badges
  const renderHistoryPills = (historyArray, label) => {
    if (!historyArray || historyArray.length === 0) {
      return null;
    }

    const colors = ['#007bff', '#28a745', '#fd7e14', '#dc3545', '#6f42c1', '#17a2b8'];
    
    return (
      <div className="grid-item grid-item--full" style={{ marginBottom: '15px' }}>
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px'
        }}>
          <div style={{
            marginBottom: '10px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#495057'
          }}>
            Previously Selected {label}:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {historyArray.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  backgroundColor: colors[index % colors.length],
                  color: '#fff',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.value}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Initialize form data with existing user data or prefilled email
  const initializeFormData = () => {
    if (existingUser) {
      return {
        id: existingUser.id || "",
        title: existingUser.title || "",
        firstName: existingUser.firstName || "",
        lastName: existingUser.lastName || "",
        email: existingUser.email || "",
        mobile: existingUser.phone || "",
        gender: existingUser.gender || "",
        country: existingUser.country || "",
        state: existingUser.state || "",
        city: existingUser.city || "",
        pincode: existingUser.zipCode || "",
        address: existingUser.address || "",
        affiliation: existingUser.affiliation || "",
        institution: existingUser.institution || "",
        specialization: "",
        designation: "",
        fullNameCertificate: existingUser.getFullName
          ? existingUser.getFullName()
          : `${existingUser.firstName || ""} ${
              existingUser.lastName || ""
            }`.trim(),
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    } else {
      return {
        id: "",
        title: "",
        firstName: "",
        lastName: "",
        email: prefilledEmail || "",
        mobile: "",
        gender: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        address: "",
        affiliation: "",
        institution: "",
        specialization: "",
        designation: "",
        fullNameCertificate: "",
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    }
  };

  const [formData, setFormData] = useState(initializeFormData());

  const {
    agreedToTerms,
    showTermsModal,
    setShowTermsModal,
    termsError,
    setTermsError,
    hasReadTerms,
    setHasReadTerms,
    pendingProceedToPayment,
    setPendingProceedToPayment,
    handleTermsAgreementChange,
    handleTermsOpen,
  } = useTermsAgreement();
  const {
    errors,
    setErrors,
    validateField,
    validateAllMandatoryFields,
  } = useRegistrationContactValidation({ formData });
  const {
    studentIdCardFile,
    setStudentIdCardFile,
    studentIdCardError,
    setStudentIdCardError,
    handleStudentIdCardUpload,
  } = useStudentIdCardUpload({ setFormData });
  const {
    savedMembershipNumber,
    setSavedMembershipNumber,
    savedStudentIdCard,
    setSavedStudentIdCard,
    savedStudentIdCardName,
    setSavedStudentIdCardName,
    savedStudentIdCardType,
    setSavedStudentIdCardType,
    setCategoriesHistory,
    setRegistrationTypesHistory,
    initialPaidCategoryId,
    setInitialPaidCategoryId,
    initialPaidRegistrationTypeId,
    setInitialPaidRegistrationTypeId,
    setInitialPaidCategoryValue,
    setInitialPaidRegistrationTypeValue,
    isCurrentRegistrationPaid,
    hasPostPaymentCategoryOrTypeChange,
    previousCategoriesHistory,
    previousRegistrationTypesHistory,
    effectiveCategoriesHistory,
    effectiveRegistrationTypesHistory,
  } = useCategoryHistory({ formData });
  const {
    bannerUrl,
    categories,
    registrationTypes,
    departments,
    countries,
    conferenceId,
    conferenceName,
    focusedSessions,
    focusedSessionAvailability,
    genderOptions,
    registrationOptionsLoading,
    registrationOptionsError,
    fetchFocusedSessions,
  } = useRegistrationLookups({
    conferenceApi: CONFERENCE_API,
    currentHost,
    setFormData,
  });
  usePaperMetadata({
    conferenceApi: CONFERENCE_API,
    conferenceId,
    paid: formData.paid,
    step,
    hasPaperSubmitted,
    setAbstractConfig,
    setAbstractStepNumber,
    setMaxAbstractChars,
    setMaxFileSizeMb,
    setPresentationMode,
    setTracks,
    setAbstractMetadataLoaded,
  });
  const {
    registerForFocusedSessions,
    setRegisterForFocusedSessions,
    selectedFocusedSessions,
    setSelectedFocusedSessions,
    focusedSessionFees,
    setFocusedSessionFees,
    fetchFocusedSessionFee,
    restoreFocusedSessionsFromFees,
    handleFocusedSessionsChange,
    handleFocusedSessionRadioChange,
  } = useFocusedSessions({
    conferenceApi: CONFERENCE_API,
    conferenceId,
    focusedSessions,
    focusedSessionAvailability,
    fetchFocusedSessions,
    additionalFees: formData.additionalFees,
    zeroPayments: formData.zeroPayments,
    accompanyingPersonFee,
    setRegistrationJustUpdated,
  });
  const {
    registrationFee,
    feeLoading,
    feeError,
    shouldApplyPaperDiscount,
    currentPaperSequence,
    paperDiscountPercentage,
    calculateOutstandingAmounts,
  } = useRegistrationFees({
    conferenceApi: CONFERENCE_API,
    registrationBaseUrl: REGISTRATION_BASE_URL,
    conferenceId,
    conferenceName,
    formData,
    abstractStepNumber,
    wantsToSubmitAbstract,
    existingPaperId,
    selectedFocusedSessions,
    accompanyingPersons,
    accompanyingPersonFee,
    setAccompanyingPersonFee,
    focusedSessionFees,
    conveyanceFeePercentage,
    hasPostPaymentCategoryOrTypeChange,
    setConveyanceFeePercentage,
  });
  useExistingRegistrationSync({
    existingUser,
    prefilledEmail,
    formData,
    setFormData,
    setIsExistingUser,
    conferenceDataLoadedRef,
    setSavedMembershipNumber,
    setSavedStudentIdCard,
    setSavedStudentIdCardName,
    setSavedStudentIdCardType,
  });
  const { checkExistingRegistration } = useExistingRegistrationCheck({
    existingUser,
    conferenceName,
    registrationBaseUrl: REGISTRATION_BASE_URL,
    conferenceId,
    accompanyingPersonFee,
    setFormData,
    setStep,
    conferenceDataLoadedRef,
    registrationCheckRequestIdRef,
    setHasPaperSubmitted,
    setPaperApprovalStatus,
    setSubmittedPapers,
    setCategoriesHistory,
    setRegistrationTypesHistory,
    setInitialPaidCategoryId,
    setInitialPaidRegistrationTypeId,
    setInitialPaidCategoryValue,
    setInitialPaidRegistrationTypeValue,
    setExistingPaperId,
    setIsPaperLocked,
    setPaperTitle,
    setAbstractText,
    setPresentationMode,
    setSubmissionMethod,
    setSelectedTrack,
    setWantsToSubmitAbstract,
    setPaperFileUploaded,
    setPaperFileName,
    setCoAuthors,
    restoreAccompanyingPersonsFromFees,
    setRegisterForFocusedSessions,
    setSelectedFocusedSessions,
    setFocusedSessionFees,
    fetchFocusedSessionFee,
    navigationDecisionRef,
    setShowPaperSubmissionSummary,
    isPendingPaperApprovalStatus,
    isApprovalMandateEnabled,
    abstractStepNumber,
    setResumePostPaymentAbstract,
    setIsInitialLoad,
    setNavigationDecisionVersion,
  });

  // Debug effect to monitor formData.paid changes
  useEffect(() => {}, [formData.paid]);

  useEffect(() => {
    if (!existingUser && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [existingUser, isInitialLoad]);

  useEffect(() => {
    if (resumePostPaymentAbstract && isPostPaymentAbstractEnabled(abstractStepNumber)) {
      setShowPaperSubmissionSummary(false);
      setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
    }
  }, [abstractStepNumber, resumePostPaymentAbstract]);

  // Basic handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "mobile") {
      nextValue = value.replace(/[^0-9]/g, "");
      if (nextValue.length > MOBILE_MAX_LENGTH) {
        nextValue = nextValue.slice(0, MOBILE_MAX_LENGTH);
      }
    }

    setFormData((p) => ({ ...p, [name]: nextValue }));

    if (["email", "mobile", "firstName", "lastName"].includes(name)) {
      validateField(name, nextValue, {
        value: nextValue,
        country: name === "mobile" ? formData.country : undefined,
      });
    }

    // Clear student ID card error if category or registration type changes away from student
    if (name === "category" || name === "registrationType") {
      const newCategory = name === "category" ? nextValue : formData.category;
      const newRegType =
        name === "registrationType" ? nextValue : formData.registrationType;

      const isStudentCategory =
        newCategory && newCategory.toLowerCase().includes("student");
      const isStudentRegType =
        newRegType && newRegType.toLowerCase().includes("student");

      // If neither is student anymore, clear the error
      if (!isStudentCategory && !isStudentRegType) {
        setStudentIdCardError("");
      }
    }
  };

  const handleCountryChange = (opt) => {
    const nextCountry = opt ? opt.value : "";
    setFormData((p) => ({ ...p, country: nextCountry }));
    validateField("country", nextCountry);
    if (formData.mobile) {
      validateField("mobile", formData.mobile, {
        country: nextCountry,
        value: formData.mobile,
      });
    }
  };


  // Process additional fees and zero payments when accompanyingPersonFee is loaded
  useEffect(() => {
    if (!accompanyingPersonFee) {
      return;
    }

    const storedAdditionalFees = formData.additionalFees || [];
    const storedZeroPayments = formData.zeroPayments || [];

    restoreAccompanyingPersonsFromFees({
      additionalFees: storedAdditionalFees,
      zeroPayments: storedZeroPayments,
    });

    if (selectedFocusedSessions.length === 0) {
      restoreFocusedSessionsFromFees({
        additionalFees: storedAdditionalFees,
        zeroPayments: storedZeroPayments,
      });
    }
  }, [
    accompanyingPersonFee,
    formData.additionalFees,
    formData.zeroPayments,
    selectedFocusedSessions.length,
    restoreAccompanyingPersonsFromFees,
    restoreFocusedSessionsFromFees,
  ]); // Run when accompanyingPersonFee loads

  const normalizedPaperApprovalStatus = (
    paperApprovalStatus || "PENDING"
  ).toUpperCase();

  const isRevisionPaperStatus =
    normalizedPaperApprovalStatus === "MINOR_REVISION" ||
    normalizedPaperApprovalStatus === "MAJOR_REVISION" ||
    normalizedPaperApprovalStatus === "REVISION_REQUESTED";
  const isDraftPaperStatus = normalizedPaperApprovalStatus === "DRAFT";
  const hasRejectedPaper = submittedPapers.some(
    (paper) => (paper.status || "").trim().toUpperCase() === "REJECTED"
  );
  const isSubmittedOrLockedPaperStatus = [
    "SUBMITTED",
    "UNDER_REVIEW",
    "ACCEPTED",
    "REJECTED",
    "REVIEWED",
  ].includes(normalizedPaperApprovalStatus);

  const readablePaperApprovalStatus =
    {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      UNDER_REVIEW: "Under Review",
      REVIEWED: "Reviewed",
      REVISION_REQUESTED: "Revision Requested",
      ACCEPTED: "Accepted",
      REJECTED: "Rejected",
    }[normalizedPaperApprovalStatus] || normalizedPaperApprovalStatus;

  const paymentBlockedPaperStatusMessage =
    normalizedPaperApprovalStatus === "REJECTED"
      ? `Paper status: ${readablePaperApprovalStatus}. Payment is unavailable because this submission was rejected. Please contact the conference organizers.`
      : normalizedPaperApprovalStatus === "REVISION_REQUESTED"
      ? `Paper status: ${readablePaperApprovalStatus}. Submit the requested revision and wait for acceptance before payment.`
      : isRevisionPaperStatus
      ? `Paper status: ${readablePaperApprovalStatus}. Please revise your paper and resubmit before payment.`
      : `Paper status: ${readablePaperApprovalStatus}. Payment will be enabled after your paper is accepted.`;

  const isPaperAwaitingReview =
    hasPaperSubmitted && isPendingPaperApprovalStatus(paperApprovalStatus);

  // In step-3 mode, payment is blocked only when metadata mandates review approval.
  const isPaymentBlockedPendingPaperApproval =
    isApprovalMandateEnabled &&
    isInlineAbstractStepEnabled(abstractStepNumber) &&
    isPaperAwaitingReview;

  const maxPaperSubmissions = Math.max(
    1,
    Number(abstractConfig?.maxSubmissions) || 1
  );
  const configuredAbstractStepNumber =
    abstractConfig?.abstractStepNumber || abstractStepNumber;
  const isPostPaymentAbstractMode = isPostPaymentAbstractEnabled(
    configuredAbstractStepNumber
  );
  const hasCompletedPayment =
    formData.paid === true ||
    formData.paid === "true" ||
    hasSuccessfulPaymentRecord(formData.payment);
  const isSavedRegistration = Boolean(formData.regId || formData.id);
  const coreLockOutstandingSummary = calculateOutstandingAmounts();
  const hasComputedZeroOutstanding =
    !feeLoading &&
    !feeError &&
    coreLockOutstandingSummary.totalOutstanding <= 0 &&
    (Boolean(registrationFee) ||
      hasCompletedPayment ||
      (Array.isArray(formData.zeroPayments) && formData.zeroPayments.length > 0));
  const shouldLockCoreRegistrationFields =
    isSavedRegistration && (hasCompletedPayment || hasComputedZeroOutstanding);
  const submittedPaperCount = submittedPapers.length;
  const allSubmittedPapersAccepted =
    submittedPaperCount > 0 &&
    submittedPapers.every(
      (paper) => (paper.status || "").trim().toUpperCase() === "ACCEPTED"
    );
  const canEditCurrentPaperSubmission =
    Boolean(existingPaperId) &&
    !isPaperLocked &&
    (isDraftPaperStatus || isRevisionPaperStatus);
  const canSubmitAnotherPostPaymentPaper =
    (isPostPaymentAbstractMode || canEditCurrentPaperSubmission) &&
    (hasCompletedPayment || canEditCurrentPaperSubmission) &&
    !hasRejectedPaper &&
    (canEditCurrentPaperSubmission ||
      (submittedPaperCount < maxPaperSubmissions &&
        (submittedPaperCount === 0 || allSubmittedPapersAccepted)));
  const shouldRenderPostPaymentAbstractForm =
    isPostPaymentAbstractMode ||
    canEditCurrentPaperSubmission ||
    resumePostPaymentAbstract;
  const canSubmitAnotherInlinePaper =
    isInlineAbstractStepEnabled(abstractStepNumber) &&
    !isPaymentBlockedPendingPaperApproval &&
    !hasRejectedPaper &&
    submittedPaperCount < maxPaperSubmissions &&
    (submittedPaperCount === 0 || allSubmittedPapersAccepted);
  const canShowPaperSubmitActions =
    !isSubmittedOrLockedPaperStatus &&
    !isPaperLocked &&
    (!existingPaperId ||
      canEditCurrentPaperSubmission ||
      canSubmitAnotherPostPaymentPaper);

  useEffect(() => {
    if (
      step === 3 &&
      wantsToSubmitAbstract === true &&
      canSubmitAnotherInlinePaper &&
      isPaperLocked &&
      allSubmittedPapersAccepted
    ) {
      resetPaperFormForNewSubmission();
      setCoAuthors([]);
      setShowPaperSubmissionSummary(false);
    }
  }, [
    allSubmittedPapersAccepted,
    canSubmitAnotherInlinePaper,
    isPaperLocked,
    resetPaperFormForNewSubmission,
    setCoAuthors,
    setShowPaperSubmissionSummary,
    step,
    wantsToSubmitAbstract,
  ]);

  const handleSubmitAnotherPostPaymentPaper = () => {
    setResumePostPaymentAbstract(false);
    resetPaperFormForNewSubmission();
    setCoAuthors([]);
    setWantsToSubmitAbstract(true);
    setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
  };

  const buildConferenceRegistrationPayload = ({
    additionalFees = null,
    includeAccompanyingPersons = false,
    includeGender = false,
    includeIsaMembershipNumber = false,
  } = {}) =>
    buildRegistrationPayload({
      formData,
      conferenceName,
      currentHost,
      accompanyingPersons,
      effectiveCategoriesHistory,
      effectiveRegistrationTypesHistory,
      additionalFees,
      includeAccompanyingPersons,
      includeGender,
      includeIsaMembershipNumber,
    });

  // Step navigation with field validation
  const validateCurrentStep = (currentStep) => {
    if (currentStep === 1) {
      const emailValid = validateField("email", formData.email);
      const mobileValid = validateField("mobile", formData.mobile, {
        country: formData.country,
        value: formData.mobile,
      });

      if (!emailValid || !mobileValid) {
        return false;
      }
    }

    const validation = validateRegistrationStep({
      currentStep,
      formData,
      genderOptions,
      abstractStepNumber:
        currentStep === POST_PAYMENT_ABSTRACT_FORM_STEP
          ? configuredAbstractStepNumber
          : abstractStepNumber,
      wantsToSubmitAbstract,
      paperTitle,
      tracks,
      selectedTrack,
      presentationMode,
      submissionMethod,
      abstractText,
      maxAbstractChars,
      paperFile,
      paperFileUploaded,
      categories,
      registrationTypes,
      accompanyingPersons,
    });

    if (validation.studentIdCardError !== null) {
      setStudentIdCardError(validation.studentIdCardError);
    }

    if (validation.abstractCharLimitError) {
      alert(validation.abstractCharLimitError);
      return false;
    }

    console.log("📋 Validation complete for step", currentStep);
    console.log("  Required fields missing:", validation.requiredFields.length);
    if (validation.requiredFields.length > 0) {
      console.log("  Missing fields:", validation.requiredFields);
      alert(
        `Please fill in the following required fields: ${validation.requiredFields.join(
          ", "
        )}`
      );
      return false;
    }

    console.log("✅ All validations passed for step", currentStep);
    return true;
  };

  const scrollToTermsCheckbox = () => {
    const termsCheckbox = document.querySelector(".terms-checkbox-container");
    if (termsCheckbox) {
      termsCheckbox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const { next, prev } = useRegistrationNavigation({
    step,
    setStep,
    totalSteps: TOTAL_STEPS,
    abstractStepNumber,
    abstractMetadataLoaded,
    validateCurrentStep,
    formData,
    isPaymentBlockedPendingPaperApproval,
    paymentBlockedPaperStatusMessage,
    agreedToTerms,
    setTermsError,
    scrollToTermsCheckbox,
    showPaperSubmissionSummary,
    setShowPaperSubmissionSummary,
    navigationDecisionRef,
    navigationDecisionVersion,
    conferenceDataLoadedRef,
    isPendingPaperApprovalStatus,
    isApprovalMandateEnabled,
    isPaperNavigationLocked: isPaymentBlockedPendingPaperApproval,
    setIsInitialLoad,
  });

  const handleProceedToPayment = async () => {
    if (isPaymentBlockedPendingPaperApproval) {
      alert(paymentBlockedPaperStatusMessage);
      return;
    }

    // Force users to read terms before they can proceed.
    if (!hasReadTerms) {
      setPendingProceedToPayment(true);
      setTermsError("Please read the Terms & Conditions before proceeding to payment.");
      setShowTermsModal(true);
      scrollToTermsCheckbox();
      return;
    }

    if (!agreedToTerms) {
      setTermsError(
        "You must agree to the Terms & Conditions before proceeding to payment."
      );
      scrollToTermsCheckbox();
      return;
    }

    const saveSuccess = await saveStep();
    if (saveSuccess) {
      setPendingProceedToPayment(false);
      setStep(7);
    }
  };

  const handleTermsAcknowledged = async () => {
    setHasReadTerms(true);

    if (isPaymentBlockedPendingPaperApproval) {
      setPendingProceedToPayment(false);
      setTermsError(paymentBlockedPaperStatusMessage);
      return;
    }

    // If user clicked "Proceed to Payment" first, continue flow right after reading terms.
    if (!pendingProceedToPayment) {
      return;
    }

    if (!agreedToTerms) {
      setPendingProceedToPayment(false);
      setTermsError(
        "Please check the Terms & Conditions agreement box to proceed to payment."
      );
      scrollToTermsCheckbox();
      return;
    }

    const saveSuccess = await saveStep();
    if (saveSuccess) {
      setPendingProceedToPayment(false);
      setTermsError("");
      setStep(7);
    }
  };

  // Combined Save & Continue function
  const saveAndContinue = async () => {
    if (!validateCurrentStep(step)) {
      return;
    }

    // Save the registration and get success status
    const saveSuccess = await saveStep();

    // If save was successful, sync registration state from backend before proceeding
    if (saveSuccess) {
      try {
        // Use latest email and conferenceName for fetch
        const emailToFetch = formData.email;
        const confNameToFetch = formData.conference || conferenceName;
	        if (emailToFetch && confNameToFetch) {
	          // Fetch latest registration from backend
	          const registrations = await fetchUserRegistrations({
	            registrationBaseUrl: REGISTRATION_BASE_URL,
	            email: emailToFetch,
	            conferenceName: confNameToFetch,
	          });
	          if (registrations.length > 0) {
            // Find the registration for this specific conference
            const existingReg = registrations.find(
              (reg) => reg.conference === confNameToFetch && !reg.cancelled
            );
            if (existingReg) {
              // Determine payment status the same way we do elsewhere
              const syncedIsPaid =
                existingReg.paid === true ||
                hasSuccessfulPaymentRecord(existingReg.payment) ||
                existingReg.confirmationDate !== null;

              // Merge registration but preserve computed/normalized fields
              setFormData((prev) => ({
                ...prev,
                ...existingReg,
                id: existingReg.registrationId,
                regId: existingReg.id,
                paid: syncedIsPaid,
                additionalFees:
                  existingReg.additionalFees && Array.isArray(existingReg.additionalFees)
                    ? existingReg.additionalFees
                    : [],
                zeroPayments:
                  existingReg.zeroPayments && Array.isArray(existingReg.zeroPayments)
                    ? existingReg.zeroPayments
                    : [],
                payment:
                  existingReg.payment && Array.isArray(existingReg.payment)
                    ? existingReg.payment
                    : [],
              }));

              // CRITICAL: Don't overwrite accompanyingPersons state here!
              // The saveStep() function already updated it properly after reloading.
              // If we overwrite here, we'll restore deleted items from the database.
              // The state should remain as the user set it, and saveStep() already synced it.

              // Only sync focused sessions (not accompanying persons)
              const storedAdditionalFees =
                existingReg.additionalFees &&
                Array.isArray(existingReg.additionalFees)
                  ? existingReg.additionalFees
                  : [];
              const storedZeroPayments =
                existingReg.zeroPayments &&
                Array.isArray(existingReg.zeroPayments)
                  ? existingReg.zeroPayments
                  : [];

              restoreFocusedSessionsFromFees({
                additionalFees: storedAdditionalFees,
                zeroPayments: storedZeroPayments,
              });
            }
          }
        }
      } catch (err) {
        // If sync fails, proceed anyway
        console.error("Failed to sync registration after save:", err);
      }

      if (
        step === 3 &&
        isInlineAbstractStepEnabled(abstractStepNumber) &&
        wantsToSubmitAbstract === true &&
        isPaymentBlockedPendingPaperApproval
      ) {
        alert(
          "Please wait for your paper approval before proceeding to registration!!!"
        );
        return;
      }

      if (step === 4 && isPaymentBlockedPendingPaperApproval) {
        alert(
          "Your paper is currently under review. You will receive an email once it has been approved. Please log in again to continue with your registration."
        );
        return;
      }
      
      if (step === 2) {
        setStep(getNextStepAfterAddress(abstractStepNumber));
        return;
      }

      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    }
  };

  const { saving, saveStep } = useRegistrationSave({
    registrationBaseUrl: REGISTRATION_BASE_URL,
    conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
    formData,
    setFormData,
    setStep,
    setErrors,
    validateField,
    buildConferenceRegistrationPayload,
    accompanyingPersons,
    accompanyingPersonFee,
    selectedFocusedSessions,
    focusedSessions,
    focusedSessionFees,
    setRegistrationJustUpdated,
    step,
    isAbstractRequired,
    wantsToSubmitAbstract,
    abstractStepNumber,
    submissionMethod,
    paperTitle,
    selectedTrack,
    abstractText,
    paperFile,
    presentationMode,
    paperSubmissionAction,
    paperApprovalStatus,
    existingPaperId,
    coAuthors,
    setExistingPaperId,
    setSubmittedPapers,
    setHasPaperSubmitted,
    setPaperApprovalStatus,
    syncAccompanyingPersonsWithReloadedRegistration,
  });

  const isPaymentUpdate = !!(formData.id || formData.regId);
  const { processingPayment, paymentInitiated, startPayment } =
    useRegistrationPayment({
      razorpayKeyId: RAZORPAY_KEY_ID,
      registrationBaseUrl: REGISTRATION_BASE_URL,
      conferenceRegistrationUrl: CONFERENCE_REGISTRATION_URL,
      orderUrl: ORDER_URL,
      paymentSaveUrl: PAYMENT_SAVE_URL,
      formData,
      setFormData,
      step,
      setStep,
      genderOptions,
      validateAllMandatoryFields,
      buildConferenceRegistrationPayload,
      isPaymentUpdate,
      isCurrentRegistrationPaid,
      calculateOutstandingAmounts,
      registrationFee,
      accompanyingPersons,
      accompanyingPersonFee,
      selectedFocusedSessions,
      focusedSessionFees,
      focusedSessions,
      conveyanceFeePercentage,
      hasPostPaymentCategoryOrTypeChange,
      shouldApplyPaperDiscount,
      paperDiscountPercentage,
      currentPaperSequence,
      abstractStepNumber,
      checkExistingRegistration,
      conferenceName,
    });

  // Check if registration has been saved (has ID)
  const hasRegistrationId = !!(formData.id || formData.regId);

  // Step fields
  const renderStepFields = () => {
    console.log("renderStepFields - step:", step, "isAbstractRequired:", isAbstractRequired);
    
    // Show paper submission summary page (step 3.x) if user just submitted paper
    if (step === 3 && showPaperSubmissionSummary && wantsToSubmitAbstract === true && hasPaperSubmitted) {
      return (
        <PaperSubmissionReviewStep
          formData={formData}
          paperTitle={paperTitle}
          selectedTrackName={selectedTrackName}
          presentationMode={presentationMode}
          submissionMethod={submissionMethod}
          normalizedPaperApprovalStatus={normalizedPaperApprovalStatus}
          paperApprovalStatus={paperApprovalStatus}
          isRevisionPaperStatus={isRevisionPaperStatus}
          submittedPapers={submittedPapers}
        />
      );
    }
    
    switch (step) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            errors={errors}
            genderOptions={genderOptions}
            mobileMaxLength={MOBILE_MAX_LENGTH}
            onChange={handleChange}
            readOnly={shouldLockCoreRegistrationFields}
          />
        );
      case 2:
        return (
          <AddressStep
            formData={formData}
            errors={errors}
            countries={countries}
            onChange={handleChange}
            onCountryChange={handleCountryChange}
            readOnly={shouldLockCoreRegistrationFields}
          />
        );
      case 3:
        return (
          <InlineAbstractSubmissionStep
            abstractStepNumber={abstractStepNumber}
            shouldSkipInlineAbstractStep={shouldSkipInlineAbstractStep}
            hasPaperSubmitted={hasPaperSubmitted}
            wantsToSubmitAbstract={wantsToSubmitAbstract}
            onChooseAbstract={() => setWantsToSubmitAbstract(true)}
            onContinueAsDelegate={() => {
              setWantsToSubmitAbstract(false);
              setStep(4);
            }}
            isPaperLocked={isPaperLocked}
            abstractConfig={abstractConfig}
            maxAbstractChars={maxAbstractChars}
            maxFileSizeMb={maxFileSizeMb}
            paperTitle={paperTitle}
            onPaperTitleChange={setPaperTitle}
            selectedTrack={selectedTrack}
            tracks={tracks}
            onSelectedTrackChange={setSelectedTrack}
            presentationMode={presentationMode}
            onPresentationModeChange={setPresentationMode}
            submissionMethod={submissionMethod}
            onSubmissionMethodChange={setSubmissionMethod}
            abstractText={abstractText}
            onAbstractTextChange={handleInlineAbstractTextChange}
            paperFileUploaded={paperFileUploaded}
            paperFile={paperFile}
            paperFileName={paperFileName}
            paperFileError={paperFileError}
            onPaperFileChange={setPaperFile}
            onPaperFileErrorChange={setPaperFileError}
            onUploadedFileDelete={handleUploadedPaperFileDelete}
            onSelectedFileRemove={handleSelectedPaperFileRemove}
            coAuthors={coAuthors}
            coAuthorErrors={coAuthorErrors}
            onAddCoAuthor={handleAddValidatedCoAuthor}
            onUpdateCoAuthor={handleUpdateValidatedCoAuthor}
            onRemoveCoAuthor={handleRemoveValidatedCoAuthor}
            conferenceId={conferenceId}
            submittedPapers={submittedPapers}
          />
        );

      case 4:
        // Always show affiliation form (summary is shown at step 3.x after submission)
        return (
          <ProfessionalInfoStep
            formData={formData}
            onChange={handleChange}
            readOnly={shouldLockCoreRegistrationFields}
          />
        );
      case 5:
      case 6:
        return (
          <>
            {/* STEP 5 - Input Fields */}
            {step === 5 && (
              <>
                {registrationOptionsLoading &&
                  categories.length === 0 &&
                  registrationTypes.length === 0 && (
                    <div className="grid-item grid-item--full">
                      <div className="registration-status warning">
                        Loading registration options...
                      </div>
                    </div>
                  )}

                {!registrationOptionsLoading &&
                  categories.length === 0 &&
                  registrationTypes.length === 0 &&
                  departments.length === 0 &&
                  focusedSessions.length === 0 && (
                    <div className="grid-item grid-item--full">
                      <div className="registration-status warning">
                        {registrationOptionsError ||
                          "No registration options are configured for this conference. Please contact support."}
                      </div>
                    </div>
                  )}

                <RegistrationRequirementNotice
                  categories={categories}
                  registrationTypes={registrationTypes}
                  formData={formData}
                />

                <DepartmentSelectionSection
                  formData={formData}
                  departments={departments}
                  onChange={handleChange}
                  readOnly={shouldLockCoreRegistrationFields}
                  onDepartmentChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedDept = departments.find(
                      (department) => department.id === parseInt(selectedId)
                    );
                    setFormData((prev) => ({
                      ...prev,
                      departmentId: selectedId ? parseInt(selectedId) : null,
                      department: selectedDept ? selectedDept.value : "",
                    }));
                  }}
                />
                <CategoryRegistrationSelectionSection
                  formData={formData}
                  categories={categories}
                  registrationTypes={registrationTypes}
                  previousCategoriesHistory={previousCategoriesHistory}
                  previousRegistrationTypesHistory={
                    previousRegistrationTypesHistory
                  }
                  renderHistoryPills={renderHistoryPills}
                  readOnly={shouldLockCoreRegistrationFields}
                  onCategoryChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedCat = categories.find(
                        (c) => c.id === parseInt(selectedId)
                      );

                      setFormData((prev) => {
                        // Save current values before switching
                        if (
                          prev.category === "ISCCM(Members)" &&
                          prev.membershipNumber
                        ) {
                          setSavedMembershipNumber(prev.membershipNumber);
                        }
                        if (
                          (prev.category === "Student" ||
                            prev.category === "student") &&
                          prev.studentIdCard
                        ) {
                          setSavedStudentIdCard(prev.studentIdCard);
                          setSavedStudentIdCardName(prev.studentIdCardName);
                          setSavedStudentIdCardType(prev.studentIdCardType);
                        }

                        // Determine values for new category
                        let membershipNumber = "";
                        let studentIdCard = null;
                        let studentIdCardName = "";
                        let studentIdCardType = "";

                        if (
                          selectedCat &&
                          selectedCat.value === "ISCCM(Members)"
                        ) {
                          // Restore saved membership number if available
                          membershipNumber =
                            savedMembershipNumber ||
                            prev.membershipNumber ||
                            "";
                        } else if (
                          selectedCat &&
                          (selectedCat.value === "Student" ||
                            selectedCat.value === "student")
                        ) {
                          // Restore saved student ID card if available
                          studentIdCard =
                            savedStudentIdCard || prev.studentIdCard || null;
                          studentIdCardName =
                            savedStudentIdCardName ||
                            prev.studentIdCardName ||
                            "";
                          studentIdCardType =
                            savedStudentIdCardType ||
                            prev.studentIdCardType ||
                            "";
                        }

                        return {
                          ...prev,
                          categoryId: selectedId ? parseInt(selectedId) : null,
                          category: selectedCat ? selectedCat.value : "",
                          membershipNumber,
                          studentIdCard,
                          studentIdCardName,
                          studentIdCardType,
                        };
                      });

                      // Clear file input and errors when not Student category or registration type
                      const isStudentSelected =
                        selectedCat &&
                        selectedCat.value.toLowerCase().includes("student");
                      const isStudentRegType =
                        formData.registrationType &&
                        formData.registrationType
                          .toLowerCase()
                          .includes("student");
                      if (!isStudentSelected && !isStudentRegType) {
                        setStudentIdCardFile(null);
                        setStudentIdCardError("");
                      }
                  }}
                  onRegistrationTypeChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedType = registrationTypes.find(
                        (rt) => rt.id === parseInt(selectedId)
                      );
                      setFormData((prev) => ({
                        ...prev,
                        registrationTypeId: selectedId
                          ? parseInt(selectedId)
                          : null,
                        registrationType: selectedType
                          ? selectedType.value
                          : "",
                      }));

                      // Clear file input and errors when not Student registration type or category
                      const isStudentRegType =
                        selectedType &&
                        selectedType.value.toLowerCase().includes("student");
                      const isStudentCategory =
                        formData.category &&
                        formData.category.toLowerCase().includes("student");
                      if (!isStudentRegType && !isStudentCategory) {
                        setStudentIdCardFile(null);
                        setStudentIdCardError("");
                      }
                  }}
                />

                <RegistrationDocumentFieldsSection
                  formData={formData}
                  studentIdCardError={studentIdCardError}
                  onChange={handleChange}
                  onStudentIdCardUpload={handleStudentIdCardUpload}
                  readOnly={shouldLockCoreRegistrationFields}
                />

                <AccompanyingPersonsInputSection
                  formData={formData}
                  accompanyingPersonFee={accompanyingPersonFee}
                  accompanyingPersons={accompanyingPersons}
                  onNameChange={handleAccompanyingPersonNameChange}
                  onRemove={handleRemoveAccompanyingPerson}
                  onAdd={handleAddAccompanyingPerson}
                />

                <FocusedSessionsInputSection
                  formData={formData}
                  focusedSessions={focusedSessions}
                  registerForFocusedSessions={registerForFocusedSessions}
                  selectedFocusedSessions={selectedFocusedSessions}
                  focusedSessionAvailability={focusedSessionAvailability}
                  accompanyingPersonFee={accompanyingPersonFee}
                  onFocusedSessionsChange={handleFocusedSessionsChange}
                  onFocusedSessionSelect={handleFocusedSessionRadioChange}
                />
              </>
            )}

            {/* STEP 6 - Summary/Review */}
            {step === 6 && (
              <div className="summary-container">
                <PersonalSummarySection formData={formData} />
                <AddressSummarySection formData={formData} />
                <ProfessionalSummarySection formData={formData} />
                <RegistrationDetailsSummarySection
                  formData={formData}
                  conferenceName={conferenceName}
                  currentHost={currentHost}
                  effectiveCategoriesHistory={effectiveCategoriesHistory}
                  effectiveRegistrationTypesHistory={
                    effectiveRegistrationTypesHistory
                  }
                  registrationBaseUrl={REGISTRATION_BASE_URL}
                />

                <RegistrationFeeSummarySection
                  formData={formData}
                  feeLoading={feeLoading}
                  feeError={feeError}
                  registrationFee={registrationFee}
                  shouldApplyPaperDiscount={shouldApplyPaperDiscount}
                  paperDiscountPercentage={paperDiscountPercentage}
                  currentPaperSequence={currentPaperSequence}
                />

                <AccompanyingPersonsSummarySection
                  formData={formData}
                  accompanyingPersons={accompanyingPersons}
                  accompanyingPersonFee={accompanyingPersonFee}
                  registrationFee={registrationFee}
                />

                <PaidFocusedSessionsSummarySection
                  formData={formData}
                  selectedFocusedSessions={selectedFocusedSessions}
                  accompanyingPersonFee={accompanyingPersonFee}
                  focusedSessions={focusedSessions}
                  focusedSessionFees={focusedSessionFees}
                  registrationFee={registrationFee}
                />

                <ZeroPaymentsSummarySection
                  formData={formData}
                  accompanyingPersons={accompanyingPersons}
                  accompanyingPersonFee={accompanyingPersonFee}
                  focusedSessions={focusedSessions}
                  focusedSessionFees={focusedSessionFees}
                  selectedFocusedSessions={selectedFocusedSessions}
                />

                <TotalAmountSummarySection
                  formData={formData}
                  registrationFee={registrationFee}
                  accompanyingPersonFee={accompanyingPersonFee}
                  focusedSessionFees={focusedSessionFees}
                  conveyanceFeePercentage={conveyanceFeePercentage}
                  calculateOutstandingAmounts={calculateOutstandingAmounts}
                />

                <PaymentStatusSummarySection formData={formData} />

                <SummaryTermsAndActionsSection
                  agreedToTerms={agreedToTerms}
                  termsError={termsError}
                  onTermsAgreementChange={handleTermsAgreementChange}
                  onTermsOpen={handleTermsOpen}
                  isPaymentBlockedPendingPaperApproval={
                    isPaymentBlockedPendingPaperApproval
                  }
                  paymentBlockedPaperStatusMessage={
                    paymentBlockedPaperStatusMessage
                  }
                  onBack={() => setStep(5)}
                  totalOutstanding={calculateOutstandingAmounts().totalOutstanding}
                  onProceedToPayment={handleProceedToPayment}
                  saving={saving}
                  canSubmitPostPaymentAbstract={
                    canSubmitAnotherPostPaymentPaper
                  }
                  onSubmitPostPaymentAbstract={() => {
                    console.log("📝 Navigating to step 7 for paper submission");
                    handleSubmitAnotherPostPaymentPaper();
                  }}
                />
              </div>
            )}
          </>
        );
      case 7: {
        let displayCurrency = "INR";
        let displaySymbol = "₹";
        let displayAmount = "0.00";

        if (registrationFee && registrationFee.rate) {
          displayCurrency = registrationFee.currency || "INR";
          displaySymbol = formData.country === "India" ? "₹" : "$";
        }

        const outstandingSummary = calculateOutstandingAmounts();
        const {
          registrationOutstanding,
          totalOutstanding,
          outstandingItems,
          totalOutstandingBeforeFee,
          conveyanceFeeAmount,
        } = outstandingSummary;

        const successfulPayments = getSuccessfulPayments(formData.payment || []);
        const paidAmountFromRecords = successfulPayments.reduce(
          (sum, p) => sum + getPaymentAmount(p),
          0
        );

        let settledPaidAmount = paidAmountFromRecords;
        if (settledPaidAmount === 0 && formData.paid === true && registrationFee) {
          const baseFee = parseFloat(registrationFee.rate || 0);
          const feeRate =
            conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
              ? conveyanceFeePercentage / 100
              : 0;
          settledPaidAmount = baseFee + baseFee * feeRate;
        }

        console.log("💳 Step 7 Payment Calculation (shared):", {
          registrationOutstanding,
          outstandingItems,
          totalOutstandingBeforeFee,
          conveyanceFeeAmount,
          totalOutstanding,
        });

        if (totalOutstanding > 0) {
          displayAmount = totalOutstanding.toFixed(2);
        } else if (formData.paid === true && settledPaidAmount > 0) {
          displayAmount = settledPaidAmount.toFixed(2);
        } else {
          displayAmount = totalOutstanding.toFixed(2);
        }

        return (
          <div className="summary-container">
            <div className="summary-section">
              <h4>Payment Details</h4>

              <div className="summary-row">
                <span className="summary-label">Registration Fee:</span>
                <span className="summary-value">
                  {displaySymbol}
                  {registrationFee && registrationFee.rate
                    ? parseFloat(registrationFee.rate).toFixed(2)
                    : "0.00"}{" "}
                  ({displayCurrency})
                </span>
              </div>

              {/* Show UNPAID accompanying persons breakdown only */}
              {accompanyingPersons.length > 0 &&
                accompanyingPersonFee &&
                (() => {
                  // Filter to only unpaid accompanying persons
                  const unpaidAccompanyingPersons = accompanyingPersons.filter(
                    (p) =>
                      p.feeTypeId === accompanyingPersonFee.id &&
                      !p.paid &&
                      !p.paymentId
                  );
                  if (unpaidAccompanyingPersons.length === 0) return null;
                  return (
                    <>
                      <div className="summary-row">
                        <span className="summary-label">
                          Accompanying Persons (
                          {unpaidAccompanyingPersons.length}):
                        </span>
                        <span className="summary-value">
                          {displaySymbol}
                          {(
                            parseFloat(accompanyingPersonFee.rate) *
                            unpaidAccompanyingPersons.length
                          ).toFixed(2)}{" "}
                          ({displayCurrency})
                        </span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-label"></span>
                        <span
                          className="summary-value"
                          style={{
                            fontSize: "0.9em",
                            color: "#666",
                            fontStyle: "italic",
                          }}
                        >
                          (
                          {unpaidAccompanyingPersons
                            .map((p) => p.name)
                            .join(", ")}
                          )
                        </span>
                      </div>
                    </>
                  );
                })()}

              {/* Show UNPAID items from additionalFees (saved but not paid) */}
              {(() => {
                const existingAdditionalFees = formData.additionalFees || [];

                // Get outstanding items (saved but not paid) excluding accompanying persons
                const outstandingFocusedSessions =
                  existingAdditionalFees.filter(
                    (fee) =>
                      !fee.paymentId &&
                      (!accompanyingPersonFee ||
                        fee.feeTypeId !== accompanyingPersonFee.id)
                  );

                if (outstandingFocusedSessions.length === 0) return null;

                // Calculate total and collect session details
                const sessionDetails = outstandingFocusedSessions.map((fee) => {
                  let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

                  // If rate is null/0, try to get it from focusedSessionFees
                  if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
                    itemRate = parseFloat(
                      focusedSessionFees[fee.feeTypeId].rate || 0
                    );
                  }

                  // Get available seats from availability data or master sessions
                  const availability = focusedSessionAvailability[fee.feeTypeId];
                  const session = focusedSessions.find((s) => s.id === fee.feeTypeId);
                  const availableSeats = Number(
                    availability?.available_seats ?? 
                    availability?.availableSeats ?? 
                    session?.availableSeats ?? 
                    session?.available_seats ?? 
                    0
                  );

                  return {
                    id: fee.id,
                    name: fee.name,
                    rate: itemRate,
                    availableSeats: availableSeats,
                  };
                });

                const outstandingTotal = sessionDetails.reduce(
                  (sum, session) => sum + session.rate,
                  0
                );

                return (
                  <>
                    <div className="summary-row">
                      <span className="summary-label">
                        Outstanding Workshops (
                        {outstandingFocusedSessions.length}):
                      </span>
                      <span className="summary-value">
                        {displaySymbol}
                        {outstandingTotal.toFixed(2)} ({displayCurrency})
                      </span>
                    </div>
                    {sessionDetails.map((session) => (
                      <div className="summary-row" key={session.id}>
                        <span
                          className="summary-label"
                          style={{
                            paddingLeft: "20px",
                            fontSize: "0.9em",
                            color: "#666",
                          }}
                        >
                          • {session.name} ({session.availableSeats} seats available)
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontSize: "0.9em", color: "#666" }}
                        >
                          {displaySymbol}
                          {session.rate.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </>
                );
              })()}

              {/* Show NEW UNPAID focused sessions from state (not yet saved) */}
              {selectedFocusedSessions.length > 0
                ? (() => {
                    const existingAdditionalFees =
                      formData.additionalFees || [];
                    const existingZeroPayments = formData.zeroPayments || [];

                    // Get IDs of sessions already in database (paid or unpaid)
                    const existingFocusedSessionIds = existingAdditionalFees
                      .filter(
                        (fee) =>
                          !accompanyingPersonFee ||
                          fee.feeTypeId !== accompanyingPersonFee.id
                      )
                      .map((fee) => fee.feeTypeId);

                    // Filter to only NEW unpaid sessions (not in database yet)
                    const newUnpaidSessions = selectedFocusedSessions.filter(
                      (sessionId) =>
                        !existingFocusedSessionIds.includes(sessionId)
                    );

                    if (newUnpaidSessions.length === 0) return null;

                    // Calculate total and collect session details
                    const sessionDetails = newUnpaidSessions.map(
                      (sessionId) => {
                        const sessionFee = focusedSessionFees[sessionId];

                        // Try to get session name from multiple sources
                        let sessionName = null;

                        // 1. Try from focusedSessions array
                        const sessionFromList = focusedSessions.find(
                          (s) => s.id === sessionId
                        );
                        if (sessionFromList) {
                          sessionName = sessionFromList.value;
                        }

                        // 2. Try from additionalFees (might have name stored)
                        if (!sessionName) {
                          const feeRecord = existingAdditionalFees.find(
                            (fee) => fee.feeTypeId === sessionId
                          );
                          if (feeRecord && feeRecord.name) {
                            sessionName = feeRecord.name;
                          }
                        }

                        // 3. Try from zeroPayments
                        if (!sessionName) {
                          const zeroRecord = existingZeroPayments.find(
                            (zp) => zp.feeTypeId === sessionId
                          );
                          if (zeroRecord && zeroRecord.name) {
                            sessionName = zeroRecord.name;
                          }
                        }

                        // 4. Fallback to session ID
                        if (!sessionName) {
                          sessionName = `Session ${sessionId}`;
                        }

                        const rate =
                          sessionFee && sessionFee.rate
                            ? parseFloat(sessionFee.rate)
                            : 0;

                        // Get available seats from availability data
                        const availability = focusedSessionAvailability[sessionId];
                        const availableSeats = Number(
                          availability?.available_seats ?? 
                          availability?.availableSeats ?? 
                          sessionFromList?.availableSeats ?? 
                          0
                        );

                        return {
                          id: sessionId,
                          name: sessionName,
                          rate: rate,
                          availableSeats: availableSeats,
                        };
                      }
                    );

                    const newUnpaidTotal = sessionDetails.reduce(
                      (sum, session) => sum + session.rate,
                      0
                    );

                    return (
                      <>
                        <div className="summary-row">
                          <span className="summary-label">
                            New Workshops ({newUnpaidSessions.length}):
                          </span>
                          <span className="summary-value">
                            {displaySymbol}
                            {newUnpaidTotal.toFixed(2)} ({displayCurrency})
                          </span>
                        </div>
                        {sessionDetails.map((session) => (
                          <div className="summary-row" key={session.id}>
                            <span
                              className="summary-label"
                              style={{
                                paddingLeft: "20px",
                                fontSize: "0.9em",
                                color: "#666",
                              }}
                            >
                              • {session.name} ({session.availableSeats} seats available)
                            </span>
                            <span
                              className="summary-value"
                              style={{ fontSize: "0.9em", color: "#666" }}
                            >
                              {displaySymbol}
                              {session.rate.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </>
                    );
                  })()
                : null}

              {feeError && (
                <div className="summary-row">
                  <span className="summary-value" style={{ color: "#dc2626" }}>
                    ⚠️ {feeError}
                  </span>
                </div>
              )}

              <div
                className="summary-row"
                style={{
                  marginTop: "1rem",
                  paddingTop: "1rem",
                  borderTop: "2px solid #e5e7eb",
                }}
              >
                <span
                  className="summary-label"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {totalOutstanding > 0
                    ? "Total Amount:"
                    : formData.paid
                    ? "Paid Amount:"
                    : "Total Amount:"}
                </span>
                <span
                  className="summary-value"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {displaySymbol}
                  {displayAmount} ({displayCurrency})
                </span>
              </div>

              {registrationFee && registrationFee.feeCategory && (
                <div className="summary-row">
                  <span className="summary-label">Fee Type:</span>
                  <span className="summary-value">
                    {registrationFee.feeCategory.value}
                  </span>
                </div>
              )}

              {registrationFee &&
                registrationFee.validFrom &&
                registrationFee.validTo && (
                  <div className="summary-row">
                    <span className="summary-label">Valid Period:</span>
                    <span className="summary-value">
                      {new Date(registrationFee.validFrom).toLocaleDateString()}{" "}
                      - {new Date(registrationFee.validTo).toLocaleDateString()}
                    </span>
                  </div>
                )}
            </div>

            <div className="summary-section">
              <h4>Registrant Information</h4>
              {formData.category && (
                <div className="summary-row">
                  <span className="summary-label">Category:</span>
                  <span className="summary-value">{formData.category}</span>
                </div>
              )}
              {formData.registrationType && formData.registrationType.trim() !== '' && formData.registrationType !== 'TBD' && (
                <div className="summary-row">
                  <span className="summary-label">Registration Type:</span>
                  <span className="summary-value">
                    {formData.registrationType}
                  </span>
                </div>
              )}
              {formData.firstName && formData.lastName && (
                <div className="summary-row">
                  <span className="summary-label">Name:</span>
                  <span className="summary-value">
                    {formData.firstName} {formData.lastName}
                  </span>
                </div>
              )}
              {formData.email && (
                <div className="summary-row">
                  <span className="summary-label">Email:</span>
                  <span className="summary-value">{formData.email}</span>
                </div>
              )}
            </div>

            <div className="summary-actions">
              <div className="summary-buttons">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startPayment}
                  disabled={
                    processingPayment ||
                    !hasRegistrationId ||
                    paymentInitiated ||
                    feeLoading ||
                    totalOutstanding <= 0
                  }
                >
                  {processingPayment
                    ? "Processing Payment..."
                    : paymentInitiated
                    ? "Payment Initiated..."
                    : feeLoading
                    ? "Calculating Fee..."
                    : totalOutstanding <= 0
                    ? "Payment Completed"
                    : !hasRegistrationId
                    ? "Save Registration First"
                    : "Pay & Complete Registration"}
                </button>
              </div>
              {!hasRegistrationId && (
                <div
                  className="registration-status warning"
                  style={{ marginTop: "1rem" }}
                >
                  ⚠️ Please go back to the Summary step and save your
                  registration before proceeding with payment.
                </div>
              )}
            </div>
          </div>
        );
      }
      case POST_PAYMENT_ABSTRACT_FORM_STEP:
        return (
          <>
            <PaymentCompletedNotice />

            {isPaperLocked && <PaperLockedNotice />}

            <AbstractSubmissionFields
              abstractConfig={abstractConfig}
              maxAbstractChars={maxAbstractChars}
              maxFileSizeMb={maxFileSizeMb}
              paperTitle={paperTitle}
              onPaperTitleChange={setPaperTitle}
              selectedTrack={selectedTrack}
              tracks={tracks}
              onSelectedTrackChange={setSelectedTrack}
              presentationMode={presentationMode}
              onPresentationModeChange={setPresentationMode}
              submissionMethod={submissionMethod}
              onSubmissionMethodChange={setSubmissionMethod}
              abstractText={abstractText}
              onAbstractTextChange={setAbstractText}
              paperFileUploaded={paperFileUploaded}
              paperFile={paperFile}
              paperFileName={paperFileName}
              paperFileError={paperFileError}
              onPaperFileChange={setPaperFile}
              onPaperFileErrorChange={setPaperFileError}
              onUploadedFileDelete={handleUploadedPaperFileDelete}
              onSelectedFileRemove={handleSelectedPaperFileRemove}
              coAuthors={coAuthors}
              onAddCoAuthor={handleAddCoAuthor}
              onUpdateCoAuthor={handleUpdateCoAuthor}
              onRemoveCoAuthor={handleRemoveCoAuthor}
              isPaperLocked={isPaperLocked}
              submittedPapers={submittedPapers}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="banner-container">
          {bannerUrl && (
            <div className="banner-container">
              <img src={bannerUrl} alt="Banner" className="banner-image" />
            </div>
          )}
        </div>
        <div className="registration-header">
          <div className="header-content">
            <h2 style={{ color: "#000", fontWeight: "bold" }}>
              Conference Registration
            </h2>
            {isExistingUser && existingUser && (
              <div className="user-info">
                <span
                  className="welcome-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Welcome back,{" "}
                  <strong>
                    {existingUser.firstName} {existingUser.lastName}
                  </strong>
                  !
                </span>
                <span className="user-email">{existingUser.email}</span>
              </div>
            )}
            {!isExistingUser && prefilledEmail && (
              <div className="user-info">
                <span
                  className="new-user-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Creating new registration for:{" "}
                  <strong>{prefilledEmail}</strong>
                </span>
              </div>
            )}
          </div>
          {onBackToLogin && (
            <button
              type="button"
              className="btn btn-outline back-to-login"
              onClick={onBackToLogin}
            >
              Logout
            </button>
          )}
        </div>

        <ProgressBar
          step={step}
          abstractStepNumber={abstractStepNumber}
          hidePostSummarySteps={
            step === 6 &&
            calculateOutstandingAmounts().totalOutstanding <= 0 &&
            !canSubmitAnotherPostPaymentPaper
          }
        />

        <form className="form-grid" onSubmit={(e) => e.preventDefault()}>
          {renderStepFields()}
        </form>

        {/* Button row - show for appropriate steps based on whether abstract is required */}
        {/* When abstract required: show for steps 1-5. When not required: show for steps 1,2,4,5 (skipping 3) */}
        {(step >= 1 && step <= 5) && (
          <div
            className="button-row"
            style={step === 1 ? { justifyContent: "flex-end" } : {}}
          >
            {step > 1 && (
              <button type="button" className="btn btn-secondary" onClick={prev}>
                Prev
              </button>
            )}

            {step === 3 && wantsToSubmitAbstract === true && canShowPaperSubmitActions ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (!validateCurrentStep(step)) {
                      return;
                    }
                    const success = await saveStep("draft");
                    if (success) {
                      setPaperApprovalStatus('DRAFT');
                      alert("Paper saved as draft. You can submit it later.");
                      setShowPaperSubmissionSummary(true);
                    }
                  }}
                  disabled={saving}
                  style={{ marginRight: '10px' }}
                >
                  {saving ? "Saving..." : "💾 Save as Draft"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!validateCurrentStep(step)) {
                      return;
                    }
                    const success = await saveStep("submit");
                    if (success) {
                      setPaperApprovalStatus('SUBMITTED');
                      alert("Paper submitted successfully. You will receive an email once it is reviewed.");
                      setShowPaperSubmissionSummary(true);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "📤 Submit"}
                </button>
              </>
            ) : (
              <>
                {step === 3 && showPaperSubmissionSummary ? (
                  <>
                    {!isPaymentBlockedPendingPaperApproval && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowPaperSubmissionSummary(false)}
                      >
                        Back to Edit
                      </button>
                    )}
                    {isPaymentBlockedPendingPaperApproval ? null : paperApprovalStatus === 'SUBMITTED' ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(4);
                        }}
                      >
                        Continue to Affiliation
                      </button>
                    ) : isRevisionPaperStatus ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(3);
                        }}
                      >
                        Continue to Edit Paper
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowPaperSubmissionSummary(false);
                          setStep(4);
                        }}
                      >
                        Continue Registration
                      </button>
                    )}
                    {canSubmitAnotherInlinePaper && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          resetPaperFormForNewSubmission();
                          setWantsToSubmitAbstract(true);
                          setShowPaperSubmissionSummary(false);
                          setStep(3);
                        }}
                      >
                        Submit Another Paper
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveAndContinue}
                    disabled={saving || (step === 4 && isPaymentBlockedPendingPaperApproval)}
                    title={step === 4 && isPaymentBlockedPendingPaperApproval ? "Please wait for paper approval before continuing" : ""}
                  >
                    {saving ? "Saving..." : (step === 3 ? "Save & continue" : "Save & Continue")}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Post-payment abstract submission actions */}
        {step === POST_PAYMENT_ABSTRACT_FORM_STEP && shouldRenderPostPaymentAbstractForm && (
          <div className="button-row">
            <button 
              className="btn btn-secondary" 
              onClick={() => setStep(6)}
            >
              Back to Summary
            </button>
            {canShowPaperSubmitActions && (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (!validateCurrentStep(POST_PAYMENT_ABSTRACT_FORM_STEP)) {
                      return;
                    }
                    
                    const success = await saveStep("draft");
                    if (success) {
                      setResumePostPaymentAbstract(false);
                      alert("Paper saved as draft.");
                      setStep(1);
                    }
                  }}
                  disabled={saving}
                  style={{
                    padding: '15px',
                    fontSize: '16px',
                    fontWeight: '600',
                    marginRight: '10px'
                  }}
                >
                  {saving ? "Saving..." : "💾 Save as Draft"}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!validateCurrentStep(POST_PAYMENT_ABSTRACT_FORM_STEP)) {
                      return;
                    }
                    
                    const success = await saveStep("submit");
                    if (success) {
                      setResumePostPaymentAbstract(false);
                      alert("Abstract submitted successfully!");
                      setStep(1);
                    }
                  }}
                  disabled={saving}
                  style={{
                    padding: '15px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  {saving ? "Saving..." : "📤 Submit"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditions
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAcknowledge={handleTermsAcknowledged}
      />
    </div>
  );
};
export default ConferenceRegistration;
